import {
  ModelFilter,
  resolveMarketingCode,
  StoredConfigurationResponse,
} from '@volkswagen-onehub/library-services-marketingcode';
import { reportErrorSentry } from 'src/bff';
import { getVicciApiKey, getVicciServiceConfig, TrackingConfiguration } from 'src/feature-app';
import { ServiceConfigurationV1 } from '@volkswagen-onehub/service-config-provider';

export interface MarketingCodeFields {
  'fields[COD_VEH_SALESGROUP]': string;
  'fields[DESC_VEH_SALESGROUP]': string;
  'fields[DESC_TRIM_LINE]': string;
  'fields[COD_VEH_TMAIMG]': string;
  'fields[DESC_VEH_TMAIMG]': string;
  'fields[DESC_VEH_MARKETING_CODE]': string;
  'fields[COD_VEH_COLOR]': string;
  'fields[DESC_VEH_COLOR]': string;
  'fields[COD_VEH_COLOR_INT]': string;
  'fields[DESC_VEH_COLOR_INT]': string;
  'fields[COD_VEH_WHEELS]': string;
  'fields[DESC_VEH_WHEELS]': string;
}

export interface MarketingCodeHistory {
  marketingCode: string;
  marketingCodeConfig: TrackingConfiguration;
  fields: MarketingCodeFields;
  response: StoredConfigurationResponse;
}
let marketingCodeHistory: MarketingCodeHistory[] = [];

export const getMarketingCode = async (
  marketingCode: string
): Promise<{
  marketingCodeConfig: TrackingConfiguration;
  fields: MarketingCodeFields;
  response: StoredConfigurationResponse;
}> => {
  // await delayRequest();
  const offerInHistory = marketingCodeHistory.find((offer) => offer.marketingCode === marketingCode);

  if (offerInHistory) {
    return {
      marketingCodeConfig: offerInHistory.marketingCodeConfig,
      fields: offerInHistory.fields,
      response: offerInHistory.response,
    };
  } else {
    const { marketingCodeConfig, fields, response } = await receiveMarketingCode(marketingCode);
    const history: MarketingCodeHistory = { marketingCode, marketingCodeConfig, fields, response };
    marketingCodeHistory.push(history);
    return { marketingCodeConfig, fields, response };
  }
};

export const getFeatures = (features: { code: string }[]) => {
  if (!features) return [];
  return features.map((feature) => feature.code);
};

export const getEquipmentlineName = (modelFilters: ModelFilter[]) => {
  if (!modelFilters) return '';
  const modelFilter: ModelFilter = modelFilters.find((modelFilter: ModelFilter) => {
    if (modelFilter.type === 'EquipmentLine') return modelFilter;
  });

  return modelFilter ? modelFilter.text : undefined;
};

export const getWheelsParams = (features: { code: string; name: string }[]) => {
  if (!features) return { COD_VEH_WHEELS: '', DESC_VEH_WHEELS: '' };
  const wheel = features.find((feature) => feature.code.startsWith('MRAD'));

  const COD_VEH_WHEELS = wheel?.code ? wheel.code : undefined;
  const DESC_VEH_WHEELS = wheel?.name ? wheel.name : undefined;
  return { COD_VEH_WHEELS, DESC_VEH_WHEELS };
};

export const getWheels = (features: { code: string; name: string }[]) => {
  const { COD_VEH_WHEELS, DESC_VEH_WHEELS } = getWheelsParams(features);

  return { 'fields[COD_VEH_WHEELS]': COD_VEH_WHEELS, 'fields[DESC_VEH_WHEELS]': DESC_VEH_WHEELS.slice(0, 65) };
};

class ErrorLoadStorageDataset extends Error {
  constructor(message: any) {
    super(message);
    this.name = 'ErrorLoadStorageDataset';
  }
}

export const receiveMarketingCode = async (
  marketingCode: string,
): Promise<{ marketingCodeConfig: TrackingConfiguration; fields: any; response?: StoredConfigurationResponse }> => {

  const vicci = getVicciServiceConfig();
  const oneApiKey = getVicciApiKey();

  const serviceConfig = {
    ...vicci,
    name: '',
    path: '/vgs',
    origin: vicci.urlOrigin,
    authentication: {      
      username: vicci?.credentials?.username,
      password: vicci?.credentials?.password,
    },
    headers: {'x-api-key': oneApiKey},
  };

  try {
    const response = await resolveMarketingCode(
      serviceConfig,
      {},
      marketingCode
    );

    const marketingCodeConfig: TrackingConfiguration = {
      SalesgroupId: response.vehicleConfiguration.salesgroupKey,
      SalesgroupName: response.consolidatedData.salesgroupName,
      EquipmentlineName: getEquipmentlineName(response.consolidatedData.modelFilters),
      ModelId: response.vehicleConfiguration.modelKey.split('$')[0],
      ModelName: response.consolidatedData.modelName,
      ModelYear: response.vehicleConfiguration.modelYear,
    };

    const fields: MarketingCodeFields = {
      'fields[COD_VEH_SALESGROUP]': response.consolidatedData?.salesgroupKey,
      'fields[DESC_VEH_SALESGROUP]': response.consolidatedData?.salesgroupName,
      'fields[DESC_TRIM_LINE]': getEquipmentlineName(response.consolidatedData?.modelFilters),
      'fields[COD_VEH_TMAIMG]': response.consolidatedData?.modelKey.split('$')[0],
      'fields[DESC_VEH_TMAIMG]': response.consolidatedData?.modelName,
      // 'fields[DESC_VEH_MARKETING_CODE]': marketingCode,
      'fields[COD_VEH_COLOR]': response.consolidatedData?.exterior?.code,
      'fields[DESC_VEH_COLOR]': response.consolidatedData?.exterior?.name,
      'fields[COD_VEH_COLOR_INT]': response.consolidatedData?.interior?.code,
      'fields[DESC_VEH_COLOR_INT]': response.consolidatedData?.interior?.name,
      ...getWheels(response.expandedConfiguration?.features),
    };

    return { marketingCodeConfig, fields, response };
  } catch (error) {
    const errorMkc = new ErrorLoadStorageDataset(error);
    reportErrorSentry(errorMkc);
    const marketingCodeConfig: TrackingConfiguration = null;
    const fields: MarketingCodeFields = null;
    return { marketingCodeConfig, fields };
  }
};

/**
 * Necesitamos retrasar la request ya que se llama desde muchos sitios al mismo tiempo, por lo que se producen llamadas
 * innecesarias. Esta función simplemente espera a que de tiempo a resolver el marketing code y se la siguiente llamada
 * ya encuentre el resultado de la request anterior en el objeto de historia.
 */
export const delayRequest = async () =>
  new Promise((resolve) => {
    setTimeout(resolve, 1000);
  });
